'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff, Loader2, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { useRouter } from 'next/navigation';


interface LoginFormData {
  email: string;
  password: string;
  rememberMe: boolean;
}

interface ForgotPasswordFormData {
  resetEmail: string;
}

interface LoginFormProps {
  onSwitchToSignup: () => void;
}

export default function LoginForm({ onSwitchToSignup }: LoginFormProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  const { signIn, signInWithGoogle, resetPassword } = useAuth();
  const router = useRouter();

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
  } = useForm<LoginFormData>({
    defaultValues: { rememberMe: false },
  });

  const {
    register: registerReset,
    handleSubmit: handleResetSubmit,
    formState: { errors: resetErrors },
    getValues: getResetValues,
  } = useForm<ForgotPasswordFormData>();

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    try {
      const result = await signIn(data.email, data.password);
      toast.success('Welcome back! Signed in successfully.');
      const role = result?.user?.user_metadata?.role || 'tenant';
      if (role === 'admin') {
        router.push('/admin');
      } else if (role === 'owner') {
        router.push('/owner-dashboard');
      } else {
        router.push('/');
      }
      router.refresh();
    } catch (err: any) {
      const rawMsg: string = err?.message || '';
      if (rawMsg.toLowerCase().includes('email not confirmed') || rawMsg.toLowerCase().includes('confirm your email')) {
        setError('email', { message: 'Your email is not confirmed yet. Please check your inbox and click the confirmation link, then try again.' });
        toast.error('Email not confirmed', { description: 'Check your inbox for a confirmation email.' });
      } else if (rawMsg.toLowerCase().includes('too many requests') || rawMsg.toLowerCase().includes('rate limit')) {
        setError('email', { message: 'Too many login attempts. Please wait a few minutes and try again.' });
      } else if (rawMsg.toLowerCase().includes('incorrect email or password')) {
        setError('email', { message: 'Incorrect email or password. Please try again.' });
      } else {
        // Show the actual error message for better diagnosis
        setError('email', { message: rawMsg || 'Sign in failed. Please try again.' });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const onResetSubmit = async (data: ForgotPasswordFormData) => {
    setResetLoading(true);
    try {
      await resetPassword(data.resetEmail);
      setResetSent(true);
      toast.success('Password reset email sent! Check your inbox.');
    } catch (err: any) {
      toast.error(err?.message || 'Failed to send reset email. Please try again.');
    } finally {
      setResetLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setGoogleLoading(true);
    try {
      await signInWithGoogle();
    } catch (err: any) {
      toast.error(err?.message || 'Google sign-in failed. Please try again.');
      setGoogleLoading(false);
    }
  };

  // ── Forgot Password View ──────────────────────────────────────────────────
  if (showForgotPassword) {
    return (
      <div className="space-y-6">
        <div>
          <button
            type="button"
            onClick={() => { setShowForgotPassword(false); setResetSent(false); }}
            className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4 transition-colors"
          >
            <ArrowLeft size={15} />
            Back to Sign In
          </button>
          <h2 className="text-2xl font-bold text-foreground">Reset your password</h2>
          <p className="text-muted-foreground text-sm mt-1">
            Enter your email and we&apos;ll send you a link to reset your password.
          </p>
        </div>

        {resetSent ? (
          <div className="rounded-xl bg-green-50 border border-green-200 p-5 text-center space-y-2">
            <div className="text-3xl">📬</div>
            <p className="font-semibold text-green-800">Check your inbox!</p>
            <p className="text-sm text-green-700">
              We sent a password reset link to <strong>{getResetValues('resetEmail')}</strong>.
              Click the link in the email to set a new password.
            </p>
            <p className="text-xs text-green-600 mt-1">Didn&apos;t receive it? Check your spam folder.</p>
          </div>
        ) : (
          <form onSubmit={handleResetSubmit(onResetSubmit)} className="space-y-5" noValidate>
            <div>
              <label htmlFor="reset-email" className="label-text">Email Address</label>
              <input
                id="reset-email"
                type="email"
                autoComplete="email"
                placeholder="you@example.com"
                className={`input-field ${resetErrors.resetEmail ? 'border-red-400 focus:ring-red-400' : ''}`}
                suppressHydrationWarning
                {...registerReset('resetEmail', {
                  required: 'Email is required',
                  pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email address' },
                })}
              />
              {resetErrors.resetEmail && (
                <p className="error-text" role="alert">{resetErrors.resetEmail.message}</p>
              )}
            </div>

            <button
              type="submit"
              disabled={resetLoading}
              className="btn-primary w-full justify-center h-11 text-base"
              suppressHydrationWarning
            >
              {resetLoading ? <><Loader2 size={16} className="animate-spin" />Sending...</> : 'Send Reset Link'}
            </button>
          </form>
        )}
      </div>
    );
  }

  // ── Normal Login View ─────────────────────────────────────────────────────
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-foreground">Welcome back</h2>
        <p className="text-muted-foreground text-sm mt-1">
          Sign in to manage your listings or find your next home
        </p>
      </div>

      {/* Password Login */}
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5" noValidate>
        <div>
          <label htmlFor="login-email" className="label-text">Email Address</label>
          <input
            id="login-email"
            type="email"
            autoComplete="email"
            placeholder="you@example.com"
            className={`input-field ${errors.email ? 'border-red-400 focus:ring-red-400' : ''}`}
            suppressHydrationWarning
            {...register('email', {
              required: 'Email is required',
              pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email address' },
            })}
          />
          {errors.email && <p className="error-text" role="alert">{errors.email.message}</p>}
        </div>

        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label htmlFor="login-password" className="label-text mb-0">Password</label>
            <button
              type="button"
              onClick={() => setShowForgotPassword(true)}
              className="text-xs text-primary font-semibold hover:underline"
              suppressHydrationWarning
            >
              Forgot password?
            </button>
          </div>
          <div className="relative">
            <input
              id="login-password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              placeholder="Enter your password"
              className={`input-field pr-11 ${errors.password ? 'border-red-400 focus:ring-red-400' : ''}`}
              suppressHydrationWarning
              {...register('password', {
                required: 'Password is required',
                minLength: { value: 6, message: 'Password must be at least 6 characters' },
              })}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {errors.password && <p className="error-text" role="alert">{errors.password.message}</p>}
        </div>

        <label className="flex items-center gap-2.5 cursor-pointer">
          <input type="checkbox" className="w-4 h-4 accent-primary rounded" suppressHydrationWarning {...register('rememberMe')} />
          <span className="text-sm text-muted-foreground font-medium">Remember me for 30 days</span>
        </label>

        <button type="submit" disabled={isLoading} className="btn-primary w-full justify-center h-11 text-base" suppressHydrationWarning>
          {isLoading ? <><Loader2 size={16} className="animate-spin" />Signing in...</> : 'Sign In'}
        </button>
      </form>

      {/* Switch to Signup */}
      <p className="text-sm text-center text-muted-foreground">
        New to Tolettu.com?{' '}
        <button onClick={onSwitchToSignup} className="text-primary font-bold hover:underline">
          Create a free account
        </button>
      </p>

      {/* Google Sign-In */}
      <div className="relative flex items-center gap-3">
        <div className="flex-1 h-px bg-border" />
        <span className="text-xs text-muted-foreground font-medium shrink-0">or continue with</span>
        <div className="flex-1 h-px bg-border" />
      </div>
      <button
        type="button"
        onClick={handleGoogleSignIn}
        disabled={googleLoading}
        suppressHydrationWarning
        className="w-full flex items-center justify-center gap-3 h-11 rounded-xl border-2 border-border bg-card hover:border-primary/40 hover:bg-secondary/30 transition-all duration-150 text-sm font-semibold text-foreground disabled:opacity-60"
      >
        {googleLoading ? (
          <Loader2 size={18} className="animate-spin text-primary" />
        ) : (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
          </svg>
        )}
        {googleLoading ? 'Redirecting...' : 'Sign in with Google'}
      </button>
    </div>
  );
}